// ============================================================================
// OFFICE HOOKS
// ============================================================================
// React hooks for the Office (Location) master.
//
// Pattern follows useEngagementTypes.ts:
// - useOffices(params)            → list with pagination, refetch
// - useOffice(id)                 → single record by id
// - useCreateOffice()             → create mutation
// - useUpdateOffice()             → update mutation
// - useDeleteOffice()             → delete mutation
//
// Note: useOffices serializes params via JSON.stringify in the dep
// array to avoid infinite re-render when callers pass an inline object.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { officeService } from 'api/services/officeService';
import type {
  ID,
  PaginatedResponse,
  Office,
  CreateOfficeRequest,
  UpdateOfficeRequest,
  OfficeSearchRequest,
} from 'api/types';

const EMPTY_PAGINATION = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/**
 * Hook to fetch a paginated list of offices.
 * Refetches automatically when `params` changes.
 */
export const useOffices = (params?: OfficeSearchRequest) => {
  const [data, setData] = useState<Office[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<Office>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchOffices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await officeService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchOffices();
  }, [fetchOffices]);

  return {
    data,
    pagination,
    loading,
    error,
    refetch: fetchOffices,
  };
};

/**
 * Hook to fetch a single office by id.
 */
export const useOffice = (id: ID | null | undefined) => {
  const [data, setData] = useState<Office | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchOffice = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await officeService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOffice();
  }, [fetchOffice]);

  return { data, loading, error, refetch: fetchOffice };
};

/**
 * Hook to create a new office.
 */
export const useCreateOffice = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createOffice = async (
    data: CreateOfficeRequest,
    options?: {
      onSuccess?: (item: Office) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await officeService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { createOffice, loading, error, success };
};

/**
 * Hook to update an office. Always pass the latest rowVersion.
 */
export const useUpdateOffice = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateOffice = async (
    id: ID,
    data: UpdateOfficeRequest,
    options?: {
      onSuccess?: (item: Office) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await officeService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { updateOffice, loading, error, success };
};

/**
 * Hook to delete an office (soft-delete on the server).
 */
export const useDeleteOffice = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteOffice = async (
    id: ID,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await officeService.delete(id);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { deleteOffice, loading, error, success };
};
